import React from 'react';
import { Formik, Form, useFormikContext, FormikConfig, FormikProps } from 'formik';

import { isFunction } from 'src/helpers';
import { useFormikAutoFocus } from 'src/hooks';
import { ErrorMessages } from '@src/features/common';
import { FormikRouteTransitionConfirmation } from './FormikRouteTransitionConfirmation';

export type FormikFormProps<T> = FormikConfig<T> & { onSubmitSuccess?: () => void } & Pick<
  BaseFormikFormProps,
  'children' | 'withExitConfirmation' | 'withErrors'
>;

export const FormikForm = <T extends {}>({
  initialValues,
  validationSchema,
  onSubmit,
  onSubmitSuccess,
  ...rest
}: FormikFormProps<T>) => {
  const handleSubmit = async (values, actions) => {
    //TODO: move handleFormikSubmit helper here
    await onSubmit(values, actions);
    //reset dirty state: github.com/jaredpalmer/formik/issues/2110
    actions.resetForm({ values });
    if (onSubmitSuccess) {
      onSubmitSuccess();
    }
  };

  return (
    <Formik onSubmit={handleSubmit} {...{ initialValues, validationSchema }}>
      {formikProps => <BaseFormikForm {...formikProps} {...rest} />}
    </Formik>
  );
};

export type BaseFormikFormProps = FormikProps<any> & {
  children: React.ReactNode | ((props: FormikProps<any>) => React.ReactNode);
  withExitConfirmation?: boolean;
  withAutoFocus?: boolean;
  withErrors?: boolean;
};

export const BaseFormikForm = ({
  children,
  withExitConfirmation = true,
  withAutoFocus = true,
  withErrors = true,
  ...formikProps
}: BaseFormikFormProps) => {
  useFormikAutoFocus(withAutoFocus);
  const { status, setStatus } = useFormikContext();

  return (
    <Form>
      {withExitConfirmation && <FormikRouteTransitionConfirmation />}
      {withErrors && <ErrorMessages errors={status?.errors} onClose={() => setStatus()} />}
      {isFunction(children) ? children(formikProps) : children}
    </Form>
  );
};
